/**
 * WebSocket Client for Real-time Notifications
 *
 * Manages WebSocket connection for real-time notification delivery.
 * Handles reconnection, authentication, and message routing.
 *
 * Why this approach works on iOS PWA (Add to Home Screen):
 * - iOS suspends WebSocket connections when the PWA is backgrounded; the socket
 *   may die without firing a reliable close event. We treat the connection as
 *   potentially stale when the page becomes visible again and force a reconnect.
 * - We do NOT rely on visibility/focus for receiving notifications; the primary
 *   path is WebSocket. Visibility is used only for recovery (reconnect after
 *   resume). We also use a polling fallback when WS is disconnected so the bell
 *   updates even if WS never reconnects (e.g. after long background).
 * - We never give up reconnecting (no hard cap on attempts) so that after
 *   network blips or iOS suspension, the app eventually re-establishes real-time
 *   delivery without requiring the user to refresh or reopen the app.
 */

import { getAuthToken } from '../utils/cookieUtils';
import { getApiUrl } from './api';

export interface NotificationMessage {
  type: 'notification' | 'count_update' | 'dm' | 'conversation_read' | 'user_presence';
  notification?: {
    id: number;
    userId: number;
    type: string;
    postId?: number;
    commentId?: number;
    actorId: number;
    read: boolean;
    createdAt: string;
    actor?: {
      id: number;
      name: string;
      username: string;
      image?: string;
      verified?: boolean;
    };
    post?: {
      id: number;
      caption: string;
      image?: string;
      video?: string;
      hashCode?: string;
    };
    comment?: {
      id: number;
      content: string;
      postId: number;
    };
  };
  count?: number;
  data?: Record<string, unknown> & {
    userId?: number;
    online?: boolean;
    conversationId?: number;
    message?: { conversationId?: number };
  };
}

type MessageHandler = (message: NotificationMessage) => void;

/** Server sends ping every 54s; consider connection dead if no message for longer. */
const STALE_CONNECTION_MS = 70_000;

class NotificationWebSocket {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 60_000; // Cap at 60s so we keep trying forever with bounded delay
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private isConnecting = false;
  private shouldReconnect = true;
  /** Last time we received any message (data or pong). Used to detect zombie connections on iOS. */
  private lastMessageTime = 0;
  private visibilityUnsubscribe: (() => void) | null = null;

  /**
   * Connect to the WebSocket server
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        const checkInterval = setInterval(() => {
          if (!this.isConnecting) {
            clearInterval(checkInterval);
            if (this.ws?.readyState === WebSocket.OPEN) {
              resolve();
            } else {
              reject(new Error('Connection failed'));
            }
          }
        }, 100);
        return;
      }

      this.isConnecting = true;
      const token = getAuthToken();
      const apiUrl = getApiUrl();
      const wsUrl = apiUrl.replace(/^http/, 'ws') + '/ws/notifications';
      // When token is in HttpOnly cookie we don't have it in JS; same-origin WS sends cookie automatically.
      const url = token ? `${wsUrl}?token=${encodeURIComponent(token)}` : wsUrl;

      try {
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          this.lastMessageTime = Date.now();
          this.setupVisibilityListener();
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.lastMessageTime = Date.now();
          try {
            const message: NotificationMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onerror = () => {
          this.isConnecting = false;
          reject(new Error('WebSocket error'));
        };

        this.ws.onclose = (event) => {
          this.isConnecting = false;
          this.ws = null;
          this.clearVisibilityListener();
          if (this.shouldReconnect && event.code !== 1000) {
            this.scheduleReconnect();
          }
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private setupVisibilityListener(): void {
    if (typeof document === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const connected = this.ws?.readyState === WebSocket.OPEN;
      const stale = this.lastMessageTime > 0 && Date.now() - this.lastMessageTime > STALE_CONNECTION_MS;
      if (!connected || stale) {
        if (this.ws) {
          this.ws.close(1000, 'Reconnect on visibility');
          this.ws = null;
        }
        this.scheduleReconnect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    this.visibilityUnsubscribe = () => document.removeEventListener('visibilitychange', onVisible);
  }

  private clearVisibilityListener(): void {
    if (this.visibilityUnsubscribe) {
      this.visibilityUnsubscribe();
      this.visibilityUnsubscribe = null;
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearVisibilityListener();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  /**
   * Schedule a reconnection attempt. No permanent cap: we keep trying with capped backoff
   * so that after network disconnect or iOS backgrounding, the app recovers without refresh.
   */
  private scheduleReconnect(): void {
    if (!this.shouldReconnect) return;

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, Math.min(this.reconnectAttempts - 1, 6)),
      this.maxReconnectDelay
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect().catch(() => {
        // scheduleReconnect will be called again from onclose
      });
    }, delay);
  }

  private handleMessage(message: NotificationMessage): void {
    this.messageHandlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        console.error('Error in message handler:', error);
      }
    });
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /** Send a JSON message to the server (e.g. subscribe_presence). No-op if not connected. */
  send(data: Record<string, unknown>): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(data));
    } catch (err) {
      console.error('WebSocket send error:', err);
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getState(): 'connecting' | 'connected' | 'disconnected' {
    if (this.isConnecting) return 'connecting';
    if (this.isConnected()) return 'connected';
    return 'disconnected';
  }

  /** For tests / debugging: time of last received message. */
  getLastMessageTime(): number {
    return this.lastMessageTime;
  }
}

export const notificationWebSocket = new NotificationWebSocket();
